import moment from "moment";
import { calculateFormula } from "services/utils/formulaUtils";
import {
  checkStatutoryFormula,
  formatMonth,
  numberOrNaN as num,
  toDisplayFormula,
  toEngineFormula,
  upcomingVersion,
  validateVersionDetails,
  versionInForce,
} from "pages/PayRoll/Shared/statutoryConfigData";

export {
  ACTIVE_STATUS_COLORS,
  VERSION_STATUS_COLORS,
  activeVersion,
  draftFrom,
  formatDay,
  formatMonth,
  isLocked,
  savedVersions,
  upcomingVersion,
  versionInForce,
  versionRange,
} from "pages/PayRoll/Shared/statutoryConfigData";

// Payroll > Configuration > Statutory > Labour Welfare Fund, on sample data
// until the LWF APIs exist (LWF Specification v1.4, screens LWF1–LWF1b).
// LWF is set up per site (work location), each site with its own versions.
// Amounts (Tamil Nadu 20 + 40, Karnataka 50 + 100) are the spec's example
// company entries, not Cauver defaults: a new site starts empty.

// ---- Options ----

export const MONTHS = moment.months();
export const MONTH_OPTIONS = MONTHS.map((month, index) => ({ value: String(index + 1), label: month }));
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export const FREQUENCY_OPTIONS = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "HALF_YEARLY", label: "Half-yearly" },
  { value: "YEARLY", label: "Yearly" },
];
// How many deduction months each frequency needs.
export const MONTH_COUNT = { MONTHLY: 12, HALF_YEARLY: 2, YEARLY: 1 };

export const APPLICABILITY_OPTIONS = [
  { value: "COVERED", label: "Covered" },
  { value: "NOT_APPLICABLE", label: "Not applicable" },
  { value: "EXEMPT", label: "Exempt" },
];
export const COVERAGE_BASIS_OPTIONS = [
  { value: "SET_DATE", label: "On the rolls on a set date" },
  { value: "ANY_DAY_IN_MONTH", label: "Employed on any day of the deduction month" },
];
export const LOW_PAY_OPTIONS = [
  { value: "OUTSTANDING", label: "Record as outstanding" },
  { value: "COMPANY_PAYS", label: "Company pays the employee share" },
];
export const AMOUNT_TYPE_OPTIONS = [
  { value: "FIXED_BAND", label: "Fixed amount by wage band" },
  { value: "PERCENT_CAP", label: "Percentage of wage with a maximum" },
];
export const ROUNDING_OPTIONS = [
  { value: "NEAREST", label: "Nearest rupee" },
  { value: "UP", label: "Round up" },
  { value: "DOWN", label: "Round down" },
];
// The company's employee categories (People › employee profile).
export const EMPLOYEE_CATEGORIES = ["Worker", "Staff", "Supervisor", "Manager", "Trainee"];
export const CATEGORY_OPTIONS = EMPLOYEE_CATEGORIES.map((category) => ({ value: category, label: category }));

// All Indian states and union territories (Cauver-maintained list).
export const STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];
export const STATE_OPTIONS = STATES.map((state) => ({ value: state, label: state }));

export const optionLabel = (options, value) => options.find((option) => option.value === value)?.label || value || "–";

// ---- Deduction months and set dates ----

export const sortMonths = (months) => [...months].sort((a, b) => Number(a) - Number(b));
export const monthName = (month) => MONTHS[Number(month) - 1] || "–";

export const deductInLabel = (version) => {
  if (!version || version.applicability !== "COVERED") return "—";
  if (version.frequency === "MONTHLY") return "Every month";
  return sortMonths(version.deduction_months).map(monthName).join(" & ") || "—";
};

// A set date is "LAST" (last day of the month) or a day number.
export const setDateLabel = (day, month) => {
  if (!day) return "–";
  if (!month) return day === "LAST" ? "Last day of each month" : `Day ${day} of each month`;
  return day === "LAST" ? `Last day of ${monthName(month)}` : `${day} ${monthName(month)}`;
};

export const setDateOptions = (month) => [
  { value: "LAST", label: setDateLabel("LAST", month) },
  ...Array.from({ length: DAYS_IN_MONTH[Number(month) - 1] || 31 }, (_, index) => ({
    value: String(index + 1),
    label: setDateLabel(String(index + 1), month),
  })),
];
// Monthly sites use one day for every month, so only days every month has.
export const MONTHLY_SET_DATE_OPTIONS = [
  { value: "LAST", label: setDateLabel("LAST") },
  ...Array.from({ length: 28 }, (_, index) => ({ value: String(index + 1), label: setDateLabel(String(index + 1)) })),
];

export const setDatesLabel = (version) => {
  if (version.coverage_basis !== "SET_DATE") return "—";
  if (version.frequency === "MONTHLY") return setDateLabel(version.set_dates[0]);
  return sortMonths(version.deduction_months)
    .map((month) => setDateLabel(version.set_dates[version.deduction_months.indexOf(month)], month))
    .join(", ");
};

// Keeps one set date per deduction month when the months change; a new
// month gets the default (last day of the month).
export const syncSetDates = (nextMonths, previousMonths, previousDates) =>
  nextMonths.map((month) => previousDates[previousMonths.indexOf(month)] || "LAST");

// Frequency change: Monthly = every month, otherwise keep what fits.
export const monthsForFrequency = (frequency, months) => {
  if (frequency === "MONTHLY") return MONTHS.map((_, index) => String(index + 1));
  const kept = months.length === 12 ? [] : sortMonths(months);
  return kept.slice(0, MONTH_COUNT[frequency] || 0);
};

// The next deduction month from today (current month included).
export const nextDeductionMonth = (version, from = moment()) => {
  if (!version || version.applicability !== "COVERED" || !version.deduction_months.length) return null;
  const months = sortMonths(version.deduction_months).map(Number);
  const current = from.month() + 1;
  const thisYear = months.find((month) => month >= current);
  return thisYear
    ? moment(from).month(thisYear - 1).startOf("month")
    : moment(from).add(1, "year").month(months[0] - 1).startOf("month");
};

// ---- Bands ----

let bandSeq = 100;
export const newBandId = () => {
  bandSeq += 1;
  return bandSeq;
};

export const emptyBand = (wageFrom = "") => ({
  id: newBandId(),
  wage_from: wageFrom,
  employee_amount: "",
  employer_amount: "",
  employee_pct: "",
  employee_max: "",
  employer_multiple: "",
  employer_max: "",
});

const fixedBand = (id, from, employee, employer) => ({ ...emptyBand(from), id, employee_amount: employee, employer_amount: employer });

export const sortBands = (bands) => [...bands].sort((a, b) => num(a.wage_from) - num(b.wage_from));

// "Wage to" is for reading only: the next band's wage from − 1 (blank =
// and above).
export const bandsWithTo = (bands) => {
  const sorted = sortBands(bands);
  return sorted.map((band, index) => ({
    ...band,
    wage_to: index < sorted.length - 1 && Number.isFinite(num(sorted[index + 1].wage_from)) ? num(sorted[index + 1].wage_from) - 1 : "",
  }));
};

const isAmount = (value) => /^\d+(\.\d{1,2})?$/.test(String(value ?? "").trim());
// Field errors for one band (Edit Band drawer). Every field of the chosen
// pattern is required (AC-L07).
export const bandFieldErrors = (band, amountType) => {
  const errors = {};
  if (!isAmount(band.wage_from)) errors.wage_from = "Enter ₹0 or more";
  if (amountType === "PERCENT_CAP") {
    const pct = num(band.employee_pct);
    if (!isAmount(band.employee_pct) || !(pct > 0 && pct <= 100)) errors.employee_pct = "More than 0, up to 100";
    if (!isAmount(band.employee_max) || !(num(band.employee_max) > 0)) errors.employee_max = "Enter the maximum (more than ₹0)";
    if (!isAmount(band.employer_multiple) || !(num(band.employer_multiple) > 0)) errors.employer_multiple = "Enter the multiple (e.g. 2)";
    if (!isAmount(band.employer_max) || !(num(band.employer_max) > 0)) errors.employer_max = "Enter the maximum (more than ₹0)";
  } else {
    if (!isAmount(band.employee_amount)) errors.employee_amount = "Enter ₹0 or more";
    if (!isAmount(band.employer_amount)) errors.employer_amount = "Enter ₹0 or more";
  }
  return errors;
};

// Whole-table check: the first band starts at ₹0 and wage-from values
// strictly increase, so there are no gaps or overlaps.
export const bandCheck = (bands, amountType) => {
  if (!bands.length) return { valid: false, message: "Add at least one band" };
  if (bands.some((band) => !isAmount(band.wage_from))) return { valid: false, message: "Every band needs a wage from (₹0 or more)" };
  const sorted = sortBands(bands);
  if (num(sorted[0].wage_from) !== 0) return { valid: false, message: "The first band must start at ₹0" };
  const duplicate = sorted.find((band, index) => index > 0 && num(band.wage_from) === num(sorted[index - 1].wage_from));
  if (duplicate) return { valid: false, message: `Two bands start at ₹${num(duplicate.wage_from).toLocaleString("en-IN")} — each band needs its own wage from` };
  const incomplete = sorted.findIndex((band) => Object.keys(bandFieldErrors(band, amountType)).length);
  if (incomplete >= 0) {
    return {
      valid: false,
      message:
        amountType === "PERCENT_CAP"
          ? `Band ${incomplete + 1}: fill employee %, employee maximum, employer multiple and employer maximum`
          : `Band ${incomplete + 1}: fill the employee and employer amounts`,
    };
  }
  return { valid: true, message: "Bands cover every wage from ₹0 with no gaps or overlaps" };
};

const inr = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

// One band in words, for Version History and the bands table.
export const bandSummary = (band, amountType) =>
  amountType === "PERCENT_CAP"
    ? `${band.employee_pct}% max ${inr(band.employee_max)} · employer ${band.employer_multiple}× max ${inr(band.employer_max)}`
    : `${inr(band.employee_amount)} + ${inr(band.employer_amount)}`;

export const bandsSummary = (bands, amountType) =>
  bandsWithTo(bands)
    .map((band) => `${inr(band.wage_from)}${band.wage_to === "" ? "+" : `–${inr(band.wage_to)}`}: ${bandSummary(band, amountType)}`)
    .join("; ") || "–";

// ---- Amounts (B5.1 steps 7–8) ----

// Rounded to paise first so floating point can't push a value over a
// half; then half up / up / down to whole rupees, once.
const toPaise = (value) => Math.round(value * 100) / 100;
export const roundRupees = (value, rounding) => {
  const paise = toPaise(value);
  if (rounding === "UP") return Math.ceil(paise);
  if (rounding === "DOWN") return Math.floor(paise);
  return Math.floor(paise + 0.5);
};

// The band with the highest wage from ≤ the LWF wage (the wage is used as
// it is, not rounded: 3,000.50 stays in the ₹0 band when the next starts at
// 3,001).
export const pickBand = (bands, wage) => {
  const sorted = sortBands(bands);
  let index = -1;
  sorted.forEach((band, position) => {
    if (num(band.wage_from) <= wage) index = position;
  });
  return index < 0 ? null : { band: sorted[index], index };
};

export const lwfAmounts = (version, wage) => {
  const picked = pickBand(version.bands, wage);
  if (!picked) return null;
  const { band } = picked;
  if (version.amount_type === "PERCENT_CAP") {
    const employee = Math.min(roundRupees((wage * num(band.employee_pct)) / 100, version.rounding), num(band.employee_max));
    const employer = Math.min(roundRupees(employee * num(band.employer_multiple), version.rounding), num(band.employer_max));
    return { band, bandIndex: picked.index, employee, employer };
  }
  return { band, bandIndex: picked.index, employee: num(band.employee_amount), employer: num(band.employer_amount) };
};

// Coverage for one employee under a site version (B5.1 steps 2, 5, 6, 8,
// 9; every test employee is on the rolls). Used by Test on employee.
export const coverageOf = (version, employee) => {
  if (version.applicability === "NOT_APPLICABLE") return { status: "NOT_APPLICABLE", text: "Site marked Not applicable → no LWF" };
  if (version.applicability === "EXEMPT") return { status: "EXEMPT_SITE", text: "Site marked Exempt → no LWF" };
  const excluded = version.excluded_categories || [];
  if (excluded.length && !employee.category) {
    return { status: "HELD_MISSING_DATA", text: "Category missing at a site that excludes categories → LWF held until it is fixed" };
  }
  if (employee.category && excluded.includes(employee.category)) {
    return { status: "NOT_COVERED_CATEGORY", text: `${employee.category} is not covered at this site → no LWF` };
  }
  const wage = employee.LWF_WAGE ?? 0;
  if (version.wage_limit !== "" && version.wage_limit != null && wage > num(version.wage_limit)) {
    return { status: "NOT_COVERED_WAGE", text: `LWF wage ${inr(wage)} is above the ${inr(version.wage_limit)} limit → no LWF` };
  }
  const amounts = lwfAmounts(version, wage);
  if (!amounts) return { status: "NO_BAND", text: "No band covers this wage" };
  if (wage <= 0 && amounts.employee > 0) {
    return version.low_pay_treatment === "COMPANY_PAYS"
      ? { status: "EE_PAID_BY_COMPANY", ...amounts, text: `no pay → company pays the employee ${inr(amounts.employee)} and its own ${inr(amounts.employer)}` }
      : { status: "EE_OUTSTANDING", ...amounts, text: `no pay → employee ${inr(amounts.employee)} recorded as outstanding, company pays ${inr(amounts.employer)}` };
  }
  return { status: "DEDUCTED", ...amounts, text: `employee ${inr(amounts.employee)} deducted, company pays ${inr(amounts.employer)}` };
};

// ---- Components ----

export const REQUIRED_CODES = ["LWF_WAGE", "EE_LWF", "ER_LWF"];
// Salary components: no suffix = monthly rate, _E = earned in the month.
export const SALARY_CODES = ["GROSS", "BASIC", "DA", "HRA", "SPECIAL"];
export const EARNED_CODES = SALARY_CODES.map((code) => `${code}_E`);
export const PAY_INPUT_CODES = ["OT", "SHIFT", "INCENTIVE"];
export const FORMULA_FUNCTIONS = ["ROUND", "ROUNDUP", "ROUNDDOWN", "MIN", "MAX", "IF"];

export const defaultComponents = () => [
  {
    code: "LWF_WAGE",
    name: "LWF Wage",
    kind: "CALCULATION",
    description: "Wage LWF is worked out on.",
    formula: "GROSS_E",
    used_for: "Earned gross for the month · used to pick the band and the coverage limit",
    status: "Active",
  },
  {
    code: "EE_LWF",
    name: "Employee LWF",
    kind: "CONTRIBUTION",
    description: "Employee share from the band the LWF wage falls in.",
    formula: "BAND(LWF_WAGE).employee",
    fixed: true,
    used_for: "Employee deduction · payslip “Labour Welfare Fund”",
    status: "Active",
  },
  {
    code: "ER_LWF",
    name: "Employer LWF",
    kind: "CONTRIBUTION",
    description: "Employer share from the band the LWF wage falls in.",
    formula: "BAND(LWF_WAGE).employer",
    fixed: true,
    used_for: "Employer cost · shown in CTC",
    status: "Active",
  },
];

export const tableFormulaOf = (component) => toDisplayFormula(component.formula);

export const calculationCodesFor = (components, ownCode) => [
  ...SALARY_CODES,
  ...EARNED_CODES,
  ...PAY_INPUT_CODES,
  ...components.filter((component) => component.kind === "CALCULATION" && component.code !== ownCode).map((component) => component.code),
];

// The band formulas aren't plain formulas; only calculation components are
// checked for loops.
const runnableFormulaOf = (component) => (component.fixed ? "0" : component.formula);

export const checkLwfFormula = (formula, components, own) =>
  checkStatutoryFormula(formula, {
    allowed: calculationCodesFor(components, own.code),
    components: components.filter((component) => component.kind === "CALCULATION"),
    own,
    formulaOf: runnableFormulaOf,
  });

// Calculation components for one test employee, in table order.
export const runCalculation = (components, employee, override) => {
  const values = { ...employee };
  components
    .filter((component) => component.kind === "CALCULATION")
    .map((component) => (override && component.code === override.code ? override : component))
    .forEach((component) => {
      if (component.status !== "Active") return;
      values[component.code] = Math.max(0, calculateFormula(toEngineFormula(component.formula), values));
    });
  return values;
};

// ---- Sites and versions ----

// Work locations (sites) and their state.
export const SITES = [
  { id: 1, name: "Chennai Office", state: "Tamil Nadu" },
  { id: 2, name: "Erode Plant", state: "Tamil Nadu" },
  { id: 3, name: "Erode Office", state: "Tamil Nadu" },
  { id: 4, name: "Bengaluru Sales Office", state: "Karnataka" },
  { id: 5, name: "Coimbatore Depot", state: "Tamil Nadu" },
];

// Fields "Copy from site" takes over (not the registration number, which
// belongs to the site).
export const RULE_FIELDS = [
  "applicability",
  "applicability_basis",
  "frequency",
  "deduction_months",
  "coverage_basis",
  "set_dates",
  "payment_due_days",
  "wage_limit",
  "excluded_categories",
  "low_pay_treatment",
  "amount_type",
  "rounding",
  "bands",
  "components",
];

const yearlyDecember = (overrides) => ({
  description: "",
  applicability: "COVERED",
  applicability_basis: "",
  confirmed_by: "Sadheesh",
  confirmed_at: "2024-12-20",
  state: "Tamil Nadu",
  registration_no: "",
  frequency: "YEARLY",
  deduction_months: ["12"],
  coverage_basis: "SET_DATE",
  set_dates: ["31"],
  payment_due_days: "31",
  wage_limit: "",
  excluded_categories: [],
  low_pay_treatment: "OUTSTANDING",
  amount_type: "FIXED_BAND",
  rounding: "NEAREST",
  bands: [fixedBand(1, "0", "20", "40")],
  components: defaultComponents(),
  copied_from: "",
  used_by: ["Dec 2025"],
  created_by: "Sadheesh",
  created_at: "2024-12-20T10:30:00",
  ...overrides,
});

export const sampleSites = () => [
  {
    ...SITES[0],
    versions: [
      yearlyDecember({
        id: 1,
        name: "Version 1",
        effective_from: "2025-01-01",
        effective_to: "",
        status: "Active",
        description: "Tamil Nadu LWF — yearly, deducted in December, as per company records.",
        applicability_basis: "Factory · 25+ employees",
        registration_no: "TN/LWF/0045821",
        excluded_categories: ["Manager"],
      }),
    ],
  },
  {
    ...SITES[1],
    versions: [
      yearlyDecember({
        id: 1,
        name: "Version 1",
        effective_from: "2025-01-01",
        effective_to: "",
        status: "Active",
        description: "Tamil Nadu LWF for the Erode plant — yearly, December.",
        applicability_basis: "Factory · 90+ employees",
        registration_no: "TN/LWF/0010293",
        excluded_categories: ["Manager"],
      }),
    ],
  },
  {
    ...SITES[2],
    versions: [
      yearlyDecember({
        id: 1,
        name: "Version 1",
        effective_from: "2025-01-01",
        effective_to: "",
        status: "Active",
        description: "Tamil Nadu LWF for the Erode office.",
        applicability_basis: "Shop & establishment · 15+ employees",
        registration_no: "TN/LWF/0010294",
      }),
    ],
  },
  {
    ...SITES[3],
    versions: [
      yearlyDecember({
        id: 1,
        name: "Version 1",
        effective_from: "2025-01-01",
        effective_to: "2025-12-31",
        status: "Superseded",
        description: "Karnataka LWF — yearly, December.",
        applicability_basis: "Shop & establishment · Karnataka",
        state: "Karnataka",
        registration_no: "KA/LWF/2210945",
        payment_due_days: "15",
        low_pay_treatment: "COMPANY_PAYS",
      }),
      yearlyDecember({
        id: 2,
        name: "Version 2",
        effective_from: "2026-01-01",
        effective_to: "",
        status: "Active",
        description: "Karnataka rates revised to ₹50 + ₹100 from 2026.",
        applicability_basis: "Shop & establishment · Karnataka",
        state: "Karnataka",
        registration_no: "KA/LWF/2210945",
        payment_due_days: "15",
        low_pay_treatment: "COMPANY_PAYS",
        bands: [fixedBand(1, "0", "50", "100")],
        used_by: [],
        confirmed_at: "2025-12-10",
        created_at: "2025-12-10T15:20:00",
      }),
    ],
  },
  { ...SITES[4], versions: [] },
];

// Set up: Version 1 as a draft with nothing filled in (no Cauver values).
export const emptyVersion = (site) => ({
  id: 1,
  name: "Version 1",
  effective_from: moment().add(1, "month").startOf("month").format("YYYY-MM-DD"),
  effective_to: "",
  status: "Draft",
  description: "",
  applicability: "",
  applicability_basis: "",
  confirmed_by: "",
  confirmed_at: "",
  state: site.state || "",
  registration_no: "",
  frequency: "",
  deduction_months: [],
  coverage_basis: "",
  set_dates: [],
  payment_due_days: "",
  wage_limit: "",
  excluded_categories: [],
  low_pay_treatment: "",
  amount_type: "FIXED_BAND",
  rounding: "NEAREST",
  bands: [emptyBand("0")],
  components: defaultComponents(),
  copied_from: "",
  used_by: [],
  created_by: "",
  created_at: "",
});

// ---- Sites list (LWF1) ----

export const SITE_STATUS_TONES = { Active: "green", "Not set up": "red", "Not applicable": "slate", Exempt: "slate" };

export const siteRow = (site) => {
  const inForce = versionInForce(site);
  const upcoming = upcomingVersion(site);
  const current = inForce || upcoming;
  if (!current) {
    return {
      id: site.id,
      site: site.name,
      state: site.state,
      registration_no: "—",
      frequency_label: "—",
      deduct_in_label: "—",
      version_label: "—",
      effective_from: "",
      status_label: "Not set up",
      status_tone: "red",
      set_up: false,
      upcoming: false,
      action: "Set up",
    };
  }
  const covered = current.applicability === "COVERED";
  const base = covered ? "Active" : optionLabel(APPLICABILITY_OPTIONS, current.applicability);
  const comingLater = Boolean(inForce && upcoming);
  return {
    id: site.id,
    site: site.name,
    state: current.state || site.state,
    registration_no: covered ? current.registration_no || "—" : "—",
    frequency_label: covered ? optionLabel(FREQUENCY_OPTIONS, current.frequency) : "—",
    deduct_in_label: deductInLabel(current),
    version_label: current.name,
    effective_from: current.effective_from,
    status_label: comingLater ? `${base} + ${upcoming.name} from ${formatMonth(upcoming.effective_from)}` : base,
    status_tone: comingLater ? "amber" : SITE_STATUS_TONES[base] || "green",
    set_up: true,
    upcoming: comingLater,
    action: "View",
  };
};

// ---- Validation (B2, B3.2; AC-L02, L05, L07) ----

export const validateSiteVersion = (form, versions) => {
  const errors = validateVersionDetails(form, versions);
  if (!form.applicability) errors.applicability = "Pick whether LWF applies to this site";
  if (!String(form.applicability_basis || "").trim()) errors.applicability_basis = "Enter the basis (e.g. Factory · 25+ employees)";
  if (!form.state) errors.state = "Pick the state";
  if (form.applicability !== "COVERED") return errors;

  const registration = String(form.registration_no || "").trim();
  if (!registration) errors.registration_no = "Enter the LWF registration number";
  else if (registration.length > 30) errors.registration_no = "30 characters at most";
  if (!form.frequency) errors.frequency = "Pick how often LWF is deducted";
  else if (new Set(form.deduction_months).size !== MONTH_COUNT[form.frequency]) {
    errors.deduction_months = form.frequency === "HALF_YEARLY" ? "Pick 2 months" : "Pick the month";
  }
  const due = String(form.payment_due_days ?? "").trim();
  if (!/^\d+$/.test(due) || Number(due) > 365) errors.payment_due_days = "Enter the days (0–365)";
  if (!form.coverage_basis) errors.coverage_basis = "Pick how coverage is decided";
  else if (form.coverage_basis === "SET_DATE") {
    const badDate = form.deduction_months.some((month, index) => {
      const day = form.set_dates[index];
      return !day || (day !== "LAST" && Number(day) > DAYS_IN_MONTH[Number(month) - 1]);
    });
    if (badDate || form.set_dates.length !== form.deduction_months.length) errors.set_dates = "Pick a set date inside each deduction month";
  }
  if (form.wage_limit !== "" && form.wage_limit != null && !(num(form.wage_limit) > 0)) errors.wage_limit = "Must be more than ₹0, or leave it blank";
  if (!form.low_pay_treatment) errors.low_pay_treatment = "Pick what happens when pay is too low";
  if (!form.amount_type) errors.amount_type = "Pick the amount type";
  const bands = bandCheck(form.bands, form.amount_type);
  if (!bands.valid) errors.bands = bands.message;
  return errors;
};

// ---- Test employees (sample payroll) ----

const testEmployee = (id, name, site, category, gross) => {
  const basic = Math.round(gross * 0.5);
  const da = Math.round(gross * 0.1);
  const hra = Math.round(gross * 0.2);
  const pay = { GROSS: gross, BASIC: basic, DA: da, HRA: hra, SPECIAL: gross - basic - da - hra };
  return {
    id,
    name,
    site,
    category,
    ...pay,
    ...Object.fromEntries(Object.entries(pay).map(([code, value]) => [`${code}_E`, value])),
    OT: 0,
    SHIFT: 0,
    INCENTIVE: 0,
  };
};

export const TEST_EMPLOYEES = [
  testEmployee("E004", "Priya N", "Chennai Office", "Worker", 6250),
  testEmployee("E150", "Arjun M", "Chennai Office", "Manager", 85000),
  testEmployee("E001", "Ravi Kumar", "Erode Plant", "Worker", 42000),
  testEmployee("E002", "Anjali Sharma", "Erode Plant", "Worker", 22500),
  testEmployee("E161", "Meena J", "Erode Plant", "", 12000),
  testEmployee("E170", "Vimal R", "Erode Plant", "Worker", 0),
  testEmployee("E095", "Divya K", "Erode Office", "Worker", 15500),
  testEmployee("E120", "Kannan V", "Bengaluru Sales Office", "Staff", 28000),
  testEmployee("E130", "Selvi K", "Coimbatore Depot", "Worker", 14000),
];

// Employees of this site first.
export const testEmployeesFor = (siteName) => [
  ...TEST_EMPLOYEES.filter((employee) => employee.site === siteName),
  ...TEST_EMPLOYEES.filter((employee) => employee.site !== siteName),
];

// "Priya N · Dec 2026: on the rolls on 31 December → employee ₹20
// deducted, company pays ₹40" for one employee under a version.
export const testLine = (version, employee) => {
  const values = runCalculation(version.components, employee);
  const result = coverageOf(version, { ...employee, LWF_WAGE: values.LWF_WAGE ?? 0 });
  const month = nextDeductionMonth(version);
  const when = month ? month.format("MMM YYYY") : "next deduction";
  const rolls =
    version.coverage_basis === "SET_DATE" && month
      ? `on the rolls on ${setDateLabel(version.set_dates[version.deduction_months.indexOf(String(month.month() + 1))], month.month() + 1)}`
      : "employed in the deduction month";
  const covered = ["DEDUCTED", "EE_OUTSTANDING", "EE_PAID_BY_COMPANY"].includes(result.status);
  return { wage: values.LWF_WAGE ?? 0, result, text: `${employee.name} · ${when}: ${covered ? `${rolls} → ` : ""}${result.text}` };
};
